import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createTheme, PaletteMode } from '@mui/material';
import { getThemeConfig } from '../assets/themes/themeConfig';
import { DarkModeThemeSync } from './launchDarkly/DarkModeToggleGate';

type ThemeContextType = {
  mode: PaletteMode;
  toggleTheme: () => void;
  setThemeMode: (mode: PaletteMode) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
  setThemeMode: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

const DarkModeThemeSyncInner = () => {
  const { setThemeMode } = useThemeMode();

  return <DarkModeThemeSync onDisabled={() => setThemeMode('light')} />;
};

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultMode?: PaletteMode;
};

export const ThemeProvider = ({ children, defaultMode = 'light' }: ThemeProviderProps) => {
  // Load theme preference from localStorage (with SSR safety)
  const [mode, setMode] = useState<PaletteMode>(() => {
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('themeMode');
      return (savedMode as PaletteMode) || defaultMode;
    }
    return defaultMode;
  });

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeMode', mode);
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const setThemeMode = (newMode: PaletteMode) => {
    setMode(newMode);
  };

  // Memoize theme to avoid recreating on every render
  const theme = useMemo(() => {
    const themeConfig = getThemeConfig(mode);
    return createTheme(themeConfig);
  }, [mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
      setThemeMode,
    }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <DarkModeThemeSyncInner />
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
